import React from 'react';
import { IconButton, Menu } from '@parsina/framework';
import { MoreHorizontal } from 'lucide-react';

import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import OverviewTab from './tabs/OverviewTab.jsx';
import HistoryTab from './tabs/HistoryTab.jsx';
import VisitsTab from './tabs/VisitsTab.jsx';
import TimelineTab from './tabs/TimelineTab.jsx';

// The four tabs behind one switch, and the ⋯ menu that sits in both headers.
//
// The TAB CONTENT is identical on both surfaces — a record is the record, and
// «مشکلات فعال» does not become a different list on a phone. What forks is the
// frame: an `InnerPageHeader` and a wide column on a desktop, a gradient
// `MobileHeader` and a single scroll under a thumb. So the surfaces own the
// frame and this owns what goes in it.

export default function RecordBody({ tab, screen, caseId, mobile = false }) {
  switch (tab) {
    case 'history':
      return (
        <HistoryTab
          caseId={caseId}
          entries={screen.entries}
          encounters={screen.encounters}
          addHistory={screen.addHistory}
          updateEntry={screen.updateEntry}
          deleteEntry={screen.deleteEntry}
        />
      );
    case 'visits':
      return (
        <VisitsTab
          encounters={screen.encounters}
          onOpenVisit={screen.openVisit}
          onStartVisit={screen.startVisit}
          canStart={!screen.archived}
          mobile={mobile}
        />
      );
    case 'timeline':
      return (
        <TimelineTab
          caseId={caseId}
          timeline={screen.timeline}
          onNavigate={screen.navigate}
        />
      );
    default:
      return (
        <OverviewTab
          caseRow={screen.caseRow}
          entries={screen.entries}
          activeProblems={screen.activeProblems}
          currentMedications={screen.currentMedications}
          pendingOrders={screen.pendingOrders}
          encounters={screen.encounters}
          threads={screen.threads}
          onOpenVisit={screen.openVisit}
          onOpenThread={screen.openChat}
        />
      );
  }
}

const MENU_ITEM = {
  display: 'block',
  width: '100%',
  textAlign: 'start',
  background: 'none',
  border: 0,
  padding: '11px 12px',
  fontSize: 13,
  cursor: 'pointer',
  color: C.textPrimary,
  font: 'inherit',
};

/** ⋯ → ویرایش بیمار · بایگانی / خروج از بایگانی · حذف. */
export function RecordMenu({ screen, onGradient = false }) {
  const { t } = useTranslation();
  const anchorRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <span ref={anchorRef} style={{ display: 'inline-flex' }}>
        <IconButton
          variant={onGradient ? 'onGradient' : 'ghost'}
          size={40}
          icon={<MoreHorizontal size={17} aria-hidden />}
          label={t('common.actions.more')}
          onClick={() => setOpen((v) => !v)}
          data-testid="record-more"
        />
      </span>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        ariaLabel={t('common.actions.more')}
        width={240}
        placement="auto"
        data-testid="record-menu"
      >
        <button
          type="button"
          role="menuitem"
          style={MENU_ITEM}
          onClick={() => { setOpen(false); screen.openEdit(); }}
          data-testid="record-edit"
        >
          {t('record.actions.edit')}
        </button>
        <button
          type="button"
          role="menuitem"
          style={MENU_ITEM}
          onClick={() => { setOpen(false); screen.toggleArchive(); }}
          data-testid="record-archive"
        >
          {t(screen.archived ? 'record.actions.unarchive' : 'record.actions.archive')}
        </button>
        <button
          type="button"
          role="menuitem"
          style={{ ...MENU_ITEM, color: C.red }}
          onClick={() => { setOpen(false); screen.openDelete(); }}
          data-testid="record-delete"
        >
          {t('record.actions.delete')}
        </button>
      </Menu>
    </>
  );
}
